import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge } from "../components/Shared";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  ChatCircleDots, HandPalm, Check, ArrowUUpLeft, Sparkle, User, Robot, Gear, PaperPlaneRight,
} from "@phosphor-icons/react";

const STATUS_TONE = { NEW: "signal", ASSIGNED: "warning", IN_PROGRESS: "warning", WAITING: "neutral", RESOLVED: "forest" };

function Bubble({ m }) {
  const inbound = m.direction === "inbound";
  const human = m.sender_type === "HUMAN";
  const system = m.sender_type === "SYSTEM" || m.internal_note;
  if (system) {
    return <div className="text-center text-[11px] text-carbon-400 italic my-2" data-testid="msg-system">{m.body}</div>;
  }
  const align = inbound ? "justify-start" : "justify-end";
  const tone = inbound ? "bg-white border-carbon-200" : human ? "bg-forest-light border-forest/30" : "bg-terracotta-light border-terracotta/30";
  const label = inbound ? "Usuario" : human ? `Humano${m.human_agent_email ? " · " + m.human_agent_email : ""}` : "IA";
  const Icon = inbound ? User : human ? HandPalm : Robot;
  return (
    <div className={`flex ${align} mb-2`}>
      <div className={`max-w-[75%] border rounded-lg px-3 py-2 ${tone}`}>
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-carbon-400 mb-0.5">
          <Icon size={11} /> {label}
        </div>
        <div className="text-sm whitespace-pre-wrap">{m.body}</div>
      </div>
    </div>
  );
}

export default function HumanInbox() {
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState("");
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState("");
  const [copilot, setCopilot] = useState(null);

  const loadList = async () => {
    const { data } = await api.get("/human-inbox/conversations", { params: statusFilter ? { status: statusFilter } : {} });
    setList(data.items || []);
  };
  useEffect(() => { loadList(); const t = setInterval(loadList, 15000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [statusFilter]);

  const openConv = async (cid) => {
    setSelected(cid); setCopilot(null); setReply("");
    const { data } = await api.get(`/human-inbox/${cid}`);
    setDetail(data);
  };

  const refreshDetail = async () => { if (selected) { const { data } = await api.get(`/human-inbox/${selected}`); setDetail(data); } loadList(); };

  const act = async (path, body, okMsg) => {
    setBusy(path);
    try { await api.post(`/human-inbox/${selected}/${path}`, body || {}); toast.success(okMsg); await refreshDetail(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(""); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy("reply");
    try { await api.post(`/human-inbox/${selected}/reply`, { body: reply }); setReply(""); toast.success("Mensaje enviado"); await refreshDetail(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error al enviar"); } finally { setBusy(""); }
  };

  const runCopilot = async (mode) => {
    setBusy("copilot-" + mode); setCopilot(null);
    try { const { data } = await api.post(`/human-inbox/${selected}/copilot`, { mode }); setCopilot(data); }
    catch (e) { toast.error("Copiloto no disponible"); } finally { setBusy(""); }
  };

  const conv = detail?.conversation;
  const u = detail?.user || {};
  const s = detail?.summary;
  const active = conv && conv.human_status !== "RESOLVED";

  return (
    <>
      <PageHeader label="Inteligencia Conversacional" title="Bandeja Humana"
        description="Conversaciones escaladas a un agente humano. La IA está pausada mientras atiendes.">
        <select data-testid="hi-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white border border-carbon-200 rounded px-3 py-2 text-sm">
          <option value="">Activos</option>
          <option value="NEW">Nuevos</option>
          <option value="IN_PROGRESS">En progreso</option>
          <option value="RESOLVED">Resueltos</option>
        </select>
      </PageHeader>
      <PageBody>
        <div className="grid grid-cols-12 gap-4" data-testid="hi-workspace">
          {/* Lista */}
          <div className="col-span-4 space-y-2" data-testid="hi-list">
            {list.length === 0 ? (
              <Card className="p-6 text-center text-carbon-500 text-sm">Sin conversaciones.</Card>
            ) : list.map((c) => (
              <button key={c.id} onClick={() => openConv(c.id)} data-testid={`hi-row-${c.id}`}
                className={`w-full text-left bg-white border rounded-lg p-3 transition-colors ${selected === c.id ? "border-terracotta" : "border-carbon-200 hover:border-carbon-300"}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="font-medium truncate">{c.user?.name || c.user?.whatsapp || "Sin nombre"}</span>
                  <Badge tone={STATUS_TONE[c.human_status] || "neutral"}>{(c.human_status || "").replace("_", " ")}</Badge>
                </div>
                <div className="text-xs text-carbon-500 truncate">{c.last_message_preview || "(sin mensajes)"}</div>
                <div className="flex items-center gap-2 mt-1.5 text-[10px] text-carbon-400">
                  <span>Score {(c.escalation || {}).score ?? "—"}</span>
                  {(c.escalation || {}).trigger && <span>· {c.escalation.trigger}</span>}
                  {c.assigned_agent_name && <span>· {c.assigned_agent_name}</span>}
                </div>
              </button>
            ))}
          </div>

          {/* Detalle */}
          <div className="col-span-8">
            {!detail ? (
              <Card className="p-10 text-center text-carbon-500">Selecciona una conversación.</Card>
            ) : (
              <div className="space-y-4">
                <Card className="p-4" testid="hi-detail-header">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-heading text-lg">{u.name || u.whatsapp}</div>
                      <div className="text-xs text-carbon-500">{u.whatsapp} · {u.public_id} · {conv.pipeline_slug || "—"}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge tone={STATUS_TONE[conv.human_status] || "neutral"}>{(conv.human_status || "").replace("_", " ")}</Badge>
                      <Badge tone={conv.ai_active ? "forest" : "signal"}>{conv.ai_active ? "IA activa" : "IA pausada"}</Badge>
                    </div>
                  </div>
                  {active && (
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <button data-testid="hi-take" onClick={() => act("take", {}, "Conversación tomada")} disabled={busy}
                        className="bg-terracotta text-white px-3 py-1.5 rounded text-xs flex items-center gap-1.5 hover:bg-terracotta-hover disabled:opacity-60">
                        <HandPalm size={13} /> {conv.assigned_agent_id === user?.id ? "Asignada a ti" : "Tomar"}
                      </button>
                      <button data-testid="hi-resolve" onClick={() => act("resolve", {}, "Resuelta")} disabled={busy}
                        className="bg-white border border-carbon-200 text-carbon-800 px-3 py-1.5 rounded text-xs flex items-center gap-1.5 hover:border-forest disabled:opacity-60">
                        <Check size={13} /> Resolver
                      </button>
                      <button data-testid="hi-return" onClick={() => act("return-to-ai", {}, "Devuelta a la IA")} disabled={busy}
                        className="bg-white border border-carbon-200 text-carbon-800 px-3 py-1.5 rounded text-xs flex items-center gap-1.5 hover:border-terracotta disabled:opacity-60">
                        <ArrowUUpLeft size={13} /> Devolver a IA
                      </button>
                    </div>
                  )}
                </Card>

                {s && (
                  <Card className="p-4 bg-page" testid="hi-summary">
                    <div className="section-label mb-2">Resumen del caso</div>
                    <div className="text-sm space-y-1">
                      <p><b>Motivo:</b> {s.motivo}</p>
                      {s.resumen && <p>{s.resumen}</p>}
                      {s.intencion && <p><b>Intención:</b> {s.intencion}</p>}
                      {s.etapa_actual && <p><b>Etapa:</b> {s.etapa_actual}</p>}
                      {(s.preguntas_pendientes || []).length > 0 && <p><b>Pendientes:</b> {s.preguntas_pendientes.join(" · ")}</p>}
                      {s.recomendacion_humano && <p className="text-terracotta"><b>Recomendación:</b> {s.recomendacion_humano}</p>}
                    </div>
                  </Card>
                )}

                {/* Copiloto */}
                <Card className="p-4" testid="hi-copilot">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkle size={15} className="text-terracotta" />
                    <span className="section-label">Copiloto (solo sugiere, no envía)</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {[["suggest_reply", "Sugerir respuesta"], ["summarize", "Resumir"], ["search_knowledge", "Buscar en conocimiento"], ["next_action", "Siguiente acción"]].map(([m, label]) => (
                      <button key={m} data-testid={`hi-copilot-${m}`} onClick={() => runCopilot(m)} disabled={busy}
                        className="bg-white border border-carbon-200 px-3 py-1.5 rounded text-xs hover:border-terracotta disabled:opacity-60">{label}</button>
                    ))}
                  </div>
                  {copilot && (
                    <div className="bg-terracotta-light border border-terracotta/30 rounded p-3 text-sm whitespace-pre-wrap" data-testid="hi-copilot-out">
                      {copilot.suggestion}
                      {copilot.mode === "suggest_reply" && active && (
                        <div className="mt-2">
                          <button onClick={() => setReply(copilot.suggestion)} className="text-xs text-terracotta underline">Usar en respuesta ↓</button>
                        </div>
                      )}
                    </div>
                  )}
                </Card>

                {/* Thread */}
                <Card className="p-4" testid="hi-thread">
                  <div className="max-h-80 overflow-auto pr-1 mb-3">
                    {(detail.messages || []).map((m) => <Bubble key={m.id} m={m} />)}
                  </div>
                  {active ? (
                    <div className="flex items-end gap-2">
                      <textarea data-testid="hi-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} rows={2}
                        placeholder="Escribe tu respuesta al usuario..." className="flex-1 bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
                      <button data-testid="hi-send" onClick={sendReply} disabled={busy === "reply" || !reply.trim()}
                        className="bg-terracotta text-white px-4 py-2.5 rounded text-sm flex items-center gap-2 hover:bg-terracotta-hover disabled:opacity-60">
                        <PaperPlaneRight size={14} /> Enviar
                      </button>
                    </div>
                  ) : (
                    <div className="text-center text-xs text-carbon-500">Conversación resuelta. La IA {conv.ai_active ? "retomó" : "quedó pausada"}.</div>
                  )}
                </Card>
              </div>
            )}
          </div>
        </div>
      </PageBody>
    </>
  );
}
